/**
 * Eva per-company DEFAULT per-user budget API client (SF-3850).
 *
 * Wraps the admin company-user-default endpoints on sf-api so the
 * company-detail page can READ the effective default per-user cap and WRITE
 * (upsert / revert) the per-company default without re-deriving paths or
 * response types inline. Mirrors evaBudgetClient.ts (the per-company OVERALL
 * cap, SF-3737); this client governs the per-company DEFAULT applied to every
 * user in the company that lacks an individual per-user override (SF-3738).
 *
 *   GET /admin/eva/company-user-default/:companyId  — perm admin:eva:budget:read
 *   PUT /admin/eva/company-user-default/:companyId  — perm admin:eva:budget:write
 *     body { defaultUserCapUsd: number|null, note?: string }
 *     number → upsert the company default; null → revert to the global default.
 *
 * Gated on the SAME admin:eva:budget:{read,write} scope as the per-company
 * override (SF-3737/3738) — both are company-level Eva budget controls.
 *
 * ⚠️ sf-api wraps every response in `{ success, data }`. The Refine
 * `dataProvider.custom` unwraps the sf-api envelope and returns `{ data:
 * innerPayload }`, so `useCustom` exposes the inner payload via
 * `query.data?.data` (same convention as the usage / override hooks). Money
 * values are fixed-precision STRINGS to avoid float drift — parse only at the
 * edge.
 */
import { useCustom, useCustomMutation } from '@refinedev/core';

import { EVA_BUDGET_READ_PERMISSION, EVA_BUDGET_WRITE_PERMISSION } from './evaBudgetClient';

/** Base path for the Eva per-company default per-user endpoints on sf-api. */
export const EVA_COMPANY_USER_DEFAULT_BASE = 'admin/eva/company-user-default';

// Re-export the shared budget permissions so consumers of this client can
// import everything from one place (the per-company default reuses the exact
// same scope as the per-company override — see SF-3737/3738).
export { EVA_BUDGET_READ_PERMISSION, EVA_BUDGET_WRITE_PERMISSION };

/**
 * Effective-default envelope returned by GET/PUT (mirrors sf-api
 * services/insights/evaCompanyUserDefaultBudgetService.js `shapeDefault`).
 *
 * - `effectiveDefaultUserCapUsd`: the per-user default actually enforced for
 *   this company's users (absent an individual override) — the per-company
 *   value when one is set, else the global per-user default. Always a string.
 * - `defaultUserCapUsd`: the per-company default value as a string when one
 *   exists, else null (no per-company default → running on the global default).
 * - `isCompanyDefault`: whether a per-company default doc currently exists.
 */
export interface EvaCompanyUserDefault {
  companyId: string;
  defaultUserCapUsd: string | null;
  effectiveDefaultUserCapUsd: string;
  isCompanyDefault: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
  note: string | null;
}

/** Body for PUT — a number upserts the default, null reverts to the global. */
export interface EvaCompanyUserDefaultUpdate {
  defaultUserCapUsd: number | null;
  note?: string;
}

/** Common options shared by the hooks. */
interface EnabledOption {
  /** Gate the request (e.g. on access-control resolution). Defaults to true. */
  enabled?: boolean;
}

/**
 * Fetch a company's effective default per-user Eva cap + per-company-default
 * state. Disabled when `companyId` is empty so the page can mount before an id
 * is available without firing a request. Returns the inner payload via
 * `query.data?.data` (sf-api envelope unwrapped by the data provider).
 * @param companyId - company ObjectId
 */
export function useCompanyUserDefault(companyId: string, options: EnabledOption = {}) {
  return useCustom<EvaCompanyUserDefault>({
    url: `/${EVA_COMPANY_USER_DEFAULT_BASE}/${companyId}`,
    method: 'get',
    queryOptions: {
      enabled: (options.enabled ?? true) && !!companyId,
      // Don't refetch on window focus: a background refetch re-seeds the edit
      // field via the control's effect and would silently discard an operator's
      // unsaved edit when they tab away and back. The value only needs to
      // (re)load on mount and after an explicit save (manual refetch).
      refetchOnWindowFocus: false,
    },
    // The control surfaces read failures inline (and a missing read perm is a
    // 403), so suppress the global error toast for this supplementary query.
    errorNotification: false,
  });
}

/**
 * Mutation to upsert (number) or revert (null) a company's default per-user
 * cap. The caller supplies the companyId + body at call time; on success the
 * control refetches. Errors (including a 403 for callers lacking
 * `admin:eva:budget:write`) are surfaced by the caller, so the global error
 * toast is suppressed here.
 */
export function useSetCompanyUserDefault() {
  return useCustomMutation<EvaCompanyUserDefault>();
}

/**
 * Build the PUT mutation args for a company-user-default write. Extracted so
 * callers and tests share one source of truth for the URL + method + body.
 * @param companyId - company ObjectId
 * @param body - the update payload (number upserts, null reverts)
 */
export function companyUserDefaultMutationArgs(
  companyId: string,
  body: EvaCompanyUserDefaultUpdate,
) {
  return {
    url: `/${EVA_COMPANY_USER_DEFAULT_BASE}/${companyId}`,
    method: 'put' as const,
    values: body,
    // Failures are surfaced inline by the control (esp. 403); no global toast.
    errorNotification: false as const,
    successNotification: false as const,
  };
}
